import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { api, unwrap } from '../../../api/client';
import type { Variant } from '../../../components/pickers';
import { useProduct } from '../api';
import type { PickedVariant } from '../lib/forms';

export interface UnitOption {
  unitId: string;
  symbol: string;
  name: string;
  factor: string;
  isDefaultPurchase: boolean;
}

/** The units an item can be bought in: its base unit plus alternate units (box of 10 …). */
export function useVariantUnits(variant: PickedVariant | null): {
  units: UnitOption[];
  loading: boolean;
  productId: string | undefined;
} {
  // Saved lines only carry the SKU: look the item up once to learn its product (and units).
  const lookup = useQuery({
    queryKey: ['variants', 'lookup', variant?.sku ?? ''],
    queryFn: () =>
      unwrap(api.GET('/variants/lookup', { params: { query: { code: variant?.sku ?? '' } } })),
    enabled: !!variant && !variant.productId && !!variant.sku,
    staleTime: 5 * 60_000,
  });
  const productId = variant?.productId ?? lookup.data?.productId;
  const product = useProduct(productId);
  const units = useMemo<UnitOption[]>(() => {
    const p = product.data;
    if (!p) return [];
    const list: UnitOption[] = [
      {
        unitId: p.baseUnit.id,
        symbol: p.baseUnit.symbol,
        name: p.baseUnit.name,
        factor: '1',
        isDefaultPurchase: false,
      },
    ];
    for (const u of p.units) {
      const factor = u.factor.replace(/\.?0+$/, '') || '1';
      const existing = list.find((x) => x.unitId === u.unitId);
      if (existing) existing.isDefaultPurchase = u.isDefaultPurchase;
      else
        list.push({
          unitId: u.unitId,
          symbol: u.symbol,
          name: u.name,
          factor,
          isDefaultPurchase: u.isDefaultPurchase,
        });
    }
    return list;
  }, [product.data]);
  return { units, loading: lookup.isLoading || product.isLoading, productId };
}

/** The unit to preselect: the default purchase unit, else the base unit. */
export function defaultUnit(units: UnitOption[]): UnitOption | undefined {
  return units.find((u) => u.isDefaultPurchase) ?? units[0];
}

/** `/variants` row → what forms keep of it. */
export function pickVariant(v: Variant | null): PickedVariant | null {
  if (!v) return null;
  return {
    id: v.id,
    productId: v.productId,
    sku: v.sku,
    displayName: v.displayName,
    purchasePrice: v.purchasePrice,
    baseUnit: v.baseUnit,
  };
}

/** Runs `apply` once the units of a newly picked item are known (sets default unit and price). */
export function useOnUnitsReady(
  variantId: string | undefined,
  units: UnitOption[],
  needsUnit: boolean,
  apply: (u: UnitOption) => void,
) {
  useEffect(() => {
    if (!variantId || !needsUnit || units.length === 0) return;
    const u = defaultUnit(units);
    if (u) apply(u);
    // apply is intentionally not a dependency: it is recreated every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantId, units, needsUnit]);
}
